import React, { useState } from "react";
import {
  Check,
  FileText,
  Settings,
  CheckCircle,
  Database,
  RotateCcw,
  X,
  Save,
  MoreVertical,
} from "lucide-react";
import { GitBranchPlus } from "lucide-react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
export interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  getStepTitle: (step: number) => string;
  onReset?: () => void;
  onCancel?: () => void;
  onSaveAndQuit?: () => void;
  isSaveDisabled?: boolean;
}

/**
 * Composant d'indicateur d'étapes moderne inspiré des designs e-commerce
 * Style horizontal avec icônes, titres
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  getStepTitle,
  onReset,
  onCancel,
  onSaveAndQuit,
  isSaveDisabled = false,
}) => {
  const { t } = useTranslation();
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
    setIsResetDialogOpen(false);
  };
  // Icônes pour chaque étape
  const getStepIcon = (
    step: number,
    isCompleted: boolean,
    isActive: boolean,
    totalSteps: number,
  ) => {
    const iconProps = {
      className: `h-5 w-5 ${
        isCompleted
          ? "text-white"
          : isActive
            ? "text-primary-foreground"
            : "text-muted-foreground"
      }`,
    };

    if (isCompleted) {
      return <Check {...iconProps} />;
    }

    switch (step) {
      case 1:
        return <Database {...iconProps} />;
      case 2:
        return totalSteps === 4 ? (
          <Settings {...iconProps} />
        ) : (
          <FileText {...iconProps} />
        );
      case 3:
        return totalSteps === 4 ? (
          <CheckCircle {...iconProps} />
        ) : (
          <Settings {...iconProps} />
        );
      case 4:
        return totalSteps === 4 ? (
          <Sparkles {...iconProps} />
        ) : totalSteps === 5 ? (
          <CheckCircle {...iconProps} />
        ) : (
          <GitBranchPlus {...iconProps} />
        );
      case 5:
        return totalSteps === 5 ? (
          <Sparkles {...iconProps} />
        ) : (
          <CheckCircle {...iconProps} />
        );
      case 6:
        return <Sparkles {...iconProps} />;
      default:
        return <FileText {...iconProps} />;
    }
  };

  return (
    <div className="w-full gap-6 items-start flex bg-background/50 rounded-lg p-6 pb-0">
      {/* Étapes avec séparateurs */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight font-sans">
          {t("dashboard.tabs.codeGenerator")}
        </h2>
        <p className="text-muted-foreground font-sans">
          {t("dashboard.tabs.codeGeneratorDescription")}
        </p>
      </div>
      <div className="flex items-start justify-between flex-1">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const isLast = index === totalSteps - 1;

          return (
            <React.Fragment key={stepNumber}>
              {/* Étape */}
              <div className="flex flex-col items-center text-center space-y-3 ">
                {/* Icône d'étape */}
                <div
                  className={`
                    relative flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300
                    ${
                      isActive
                        ? "border-primary bg-primary shadow-lg"
                        : isCompleted
                          ? "border-green-500 bg-green-500 shadow-md"
                          : "border-muted bg-muted/50"
                    }
                  `}
                >
                  {getStepIcon(stepNumber, isCompleted, isActive, totalSteps)}
                </div>

                {/* Contenu de l'étape */}
                <div className="space-y-1 max-w-[140px]">
                  {/* Titre */}
                  <h3
                    className={`
                      text-sm font-semibold leading-tight
                      ${
                        isActive
                          ? "text-foreground"
                          : isCompleted
                            ? "text-foreground/80"
                            : "text-muted-foreground"
                      }
                    `}
                  >
                    {getStepTitle(stepNumber)}
                  </h3>

                  {/* Description */}
                  {/* <p
                    className={`
                      text-xs leading-relaxed
                      ${
                        isActive
                          ? "text-foreground/70"
                          : isCompleted
                          ? "text-muted-foreground"
                          : "text-muted-foreground/60"
                      }
                    `}
                  >
                    {getStepDescription(stepNumber)}
                  </p> */}
                </div>
              </div>

              {/* Séparateur horizontal entre les étapes */}
              {!isLast && (
                <div className="flex items-center justify-center pt-5 flex-1">
                  {(() => {
                    const nextStepNumber = stepNumber + 1;
                    const nextIsActive = nextStepNumber === currentStep;
                    const nextIsCompleted = nextStepNumber < currentStep;

                    // Le séparateur est coloré si l'étape actuelle ET la suivante sont actives ou complétées
                    const shouldBeColored =
                      (isActive || isCompleted) &&
                      (nextIsActive || nextIsCompleted);

                    return (
                      <div
                        className={`h-0.5 flex-1 mx-2 transition-colors duration-300 ${
                          shouldBeColored
                            ? "bg-primary"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                    );
                  })()}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="end">
          <div className="flex flex-col gap-1">
            {onCancel && (
              <Button
                variant="ghost"
                onClick={onCancel}
                className="w-full justify-start gap-2"
              >
                <X className="h-4 w-4" />
                {t("wizardFooter.cancel")}
              </Button>
            )}
            {onSaveAndQuit && (
              <Button
                variant="ghost"
                onClick={onSaveAndQuit}
                disabled={isSaveDisabled}
                className="w-full justify-start gap-2"
              >
                <Save className="h-4 w-4" />
                {t("wizardFooter.saveAndQuit")}
              </Button>
            )}
            <Dialog
              open={isResetDialogOpen}
              onOpenChange={setIsResetDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2 text-destructive hover:bg-destructive hover:text-white"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("wizard.resetDialog.confirm", "Réinitialiser")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <RotateCcw className="h-5 w-5 text-destructive" />
                    {t(
                      "wizard.resetDialog.title",
                      "Réinitialiser le formulaire",
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      "wizard.resetDialog.description",
                      "Êtes-vous sûr de vouloir réinitialiser le formulaire ? Toutes les données saisies seront perdues et le formulaire reviendra à son état initial.",
                    )}
                  </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        {t(
                          "wizard.resetDialog.warning",
                          "Cette action est irréversible. Toutes vos modifications seront perdues.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsResetDialogOpen(false)}
                  >
                    {t("common.cancel", "Annuler")}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleReset}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t("wizard.resetDialog.confirm", "Réinitialiser")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
